import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import BlogMarkdown from '../lib/BlogMarkdown';
import {
  createBlog, updateBlog, getBlogForEdit, type BlogDraft,
} from '../lib/blogsApi';
import './Blog.css';

// Admin Markdown editor with a live split-pane preview. Used for both authoring
// (route /blogs/new) and editing (route /blogs/:slug/edit). The route guard in
// App.tsx already restricts this to admins.
const BlogEditorPage: React.FC = () => {
  const { slug } = useParams();
  const isEdit = !!slug;
  const navigate = useNavigate();
  const { state } = useAuth();
  const token = state.kind === 'authenticated' ? state.idToken : undefined;
  useDocumentTitle(isEdit ? 'edit post' : 'new post');

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [tags, setTags] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isEdit || !slug) return;
    let cancelled = false;
    (async () => {
      try {
        const post = await getBlogForEdit(slug, token);
        if (cancelled) return;
        setTitle(post.title);
        setSummary(post.summary);
        setCoverUrl(post.cover_image_url);
        setTags((post.tags || []).join(', '));
        setBody(post.body_markdown);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load post.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, slug, token]);

  const draft = useCallback((publish?: boolean): BlogDraft => ({
    title: title.trim(),
    summary: summary.trim(),
    body_markdown: body,
    cover_image_url: coverUrl.trim(),
    tags: tags.split(',').map(t => t.trim()).filter(Boolean),
    ...(publish === undefined ? {} : { publish }),
  }), [title, summary, body, coverUrl, tags]);

  const save = useCallback(async (publish: boolean | undefined) => {
    if (!title.trim()) { setError('A title is required.'); return; }
    setSaving(true); setError(null);
    try {
      const post = isEdit && slug
        ? await updateBlog(slug, draft(publish), token)
        : await createBlog(draft(publish ?? false), token);
      // Published → go read it; saved draft → back to the list.
      navigate(post.status === 'published' ? `/blogs/${post.slug}` : '/blogs');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [isEdit, slug, draft, token, navigate, title]);

  // Wrap the current selection (or insert at caret) with markdown syntax.
  const wrap = (before: string, after = before) => {
    const ta = bodyRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value } = ta;
    const next = value.slice(0, s) + before + value.slice(s, e) + after + value.slice(e);
    setBody(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = s + before.length;
      ta.selectionEnd = e + before.length;
    });
  };

  if (loading) return <div className="blog-page blog-muted">Loading…</div>;

  return (
    <div className="blog-editor">
      <div className="blog-editor-head">
        <h1 className="blog-list-title">{isEdit ? 'edit post' : 'new post'}</h1>
        <div className="blog-editor-actions">
          <button className="blog-btn ghost" onClick={() => navigate('/blogs')} disabled={saving}>Cancel</button>
          <button className="blog-btn ghost" onClick={() => save(false)} disabled={saving}>Save draft</button>
          <button className="blog-btn primary" onClick={() => save(true)} disabled={saving}>
            {saving ? 'Saving…' : 'Publish'}
          </button>
        </div>
      </div>

      {error && <div className="blog-error">{error}</div>}

      <div className="blog-editor-fields">
        <input className="blog-input" placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
        <input className="blog-input" placeholder="Summary (also the social-card description)" value={summary} onChange={e => setSummary(e.target.value)} />
        <input className="blog-input" placeholder="Cover image URL (optional)" value={coverUrl} onChange={e => setCoverUrl(e.target.value)} />
        <input className="blog-input" placeholder="Tags, comma-separated" value={tags} onChange={e => setTags(e.target.value)} />
      </div>

      <div className="blog-editor-split">
        <div className="blog-editor-pane">
          <div className="blog-md-toolbar">
            <button onClick={() => wrap('**')} title="Bold"><b>B</b></button>
            <button onClick={() => wrap('_')} title="Italic"><i>I</i></button>
            <button onClick={() => wrap('## ', '')} title="Heading">H</button>
            <button onClick={() => wrap('[', '](https://)')} title="Link">🔗</button>
            <button onClick={() => wrap('![alt](', ')')} title="Image">🖼</button>
            <button onClick={() => wrap('`')} title="Code">{'</>'}</button>
          </div>
          <textarea
            ref={bodyRef}
            className="blog-md-textarea"
            placeholder="Write your post in Markdown…"
            value={body}
            onChange={e => setBody(e.target.value)}
          />
        </div>
        <div className="blog-editor-pane blog-editor-preview">
          <div className="blog-preview-label">preview</div>
          <BlogMarkdown text={body} />
        </div>
      </div>
    </div>
  );
};

export default BlogEditorPage;
